<template>
  <div>
    <form id="login-form" class="space-y-3" autocomplete="off" @submit="onSubmit">
      <FormField v-slot="{ componentField, errorMessage }" name="username">
        <FormItem>
          <FormControl>
            <Input
              v-bind="componentField"
              :key="'login-username-input'"
              class="h-12 rounded-9xl"
              type="text"
              :placeholder="$t('home.user.usernamePlaceholder')"
              autocomplete="username"
              :error-message="errorMessage"
              :tooltip="$t('home.user.validate.usernameValid')"
            >
              <template #prefix>
                <Icon icon="Function-Wireframe-User" class="size-4" />
              </template>
            </Input>
          </FormControl>
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField, errorMessage }" name="password">
        <FormItem>
          <FormControl>
            <div class="relative items-center">
              <Input
                v-bind="componentField"
                :key="'login-password-input'"
                class="h-12 rounded-9xl"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('home.user.passwordPlaceholder')"
                autocomplete="current-password"
                :error-message="errorMessage"
                :tooltip="$t('home.user.validate.passwordValid')"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="showPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                    class="size-4 text-muted-foreground cursor-pointer pointer-events-auto"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </Input>
            </div>
          </FormControl>
        </FormItem>
      </FormField>

      <Button color="black" class="w-full h-12 mt-8" :disabled="!meta.valid">
        <Icon icon="Function-Wireframe-User" class="mr-0.5 size-6" />
        {{ $t('home.user.loginForm.login') }}
      </Button>
    </form>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/zod'
  import { z } from 'zod'
  import { Button, FormControl, FormField, FormItem, Icon, Input, toast } from '@rapoo/element'
  import { useLocaleStore, useUserStore } from '@/stores'
  import { useRouter } from 'vue-router'

  const userStore = useUserStore()
  const { t } = useLocaleStore()
  const router = useRouter()
  const showPassword = ref(false)

  // 登录不校验账号名的长度和字符规则，因为存在一些不符合规则的历史账号，规则在注册时约束即可
  // const OLD_VALIDATION = {
  //   username: z.string().min(6).regex(/^[a-zA-Z0-9@.]+$/),
  //   password: z.string().regex(/^[a-zA-Z0-9!?@#￥%&*()]+$/).min(6).max(20),
  // }

  const formSchema = toTypedSchema(
    z.object({
      username: z
        .string({
          message: t('home.user.validate.username'),
        })
        .min(1, { message: t('home.user.validate.username') }),
      password: z
        .string({
          message: t('home.user.validate.password'),
        })
        .min(1, { message: t('home.user.validate.password') }),
    }),
  )

  const { handleSubmit, meta } = useForm({
    validationSchema: formSchema,
    initialValues: {
      username: '',
      password: '',
    },
  })

  const onSubmit = handleSubmit((values) => {
    userStore
      .login(values)
      .then(() => {
        toast.success(t('home.user.loginForm.loginSuccessful'))
        router.replace('/')
      })
      .catch((err: any) => {
        toast.error(err.message || t('home.user.loginForm.loginFailed'))
      })
  })
</script>
